import { type FC } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { sort } from 'app/utils/array';
import sharedStyle from 'app/components/DesignVersion/Grad/Blocks/PlayersHeadToHeadBlockGrad/style.module.scss';
import { roundForDisplay } from 'app/utils/number';
import { localizedHeroName } from 'app/i18n/utils';
import { getCDNUrl } from 'app/utils/api_url';

import style from './style.module.scss';

const NUMBER_OF_PICKED_HEROES_DISPLAYED = 3;

interface Props {
  playerTeam1: StatPlayerDota2 | undefined;
  playerTeam2: StatPlayerDota2 | undefined;
}

const Dota2PlayersHeroesStats: FC<Props> = ({ playerTeam1, playerTeam2 }) => {
  const { t } = useTranslation();

  const playerTeam1MostPickedHeroes = sort(playerTeam1?.most_picked || [], hero =>
    'presence_percentage' in hero ? hero.presence_percentage : true
  ).slice(0, NUMBER_OF_PICKED_HEROES_DISPLAYED);
  const playerTeam2MostPickedHeroes = sort(playerTeam2?.most_picked || [], hero =>
    'presence_percentage' in hero ? hero.presence_percentage : true
  ).slice(0, NUMBER_OF_PICKED_HEROES_DISPLAYED);

  return (
    <div className={sharedStyle.videogameSpecificStatsTables}>
      {[playerTeam1MostPickedHeroes, playerTeam2MostPickedHeroes].map(
        (playerMostPickedHeroes, index) => (
          <div
            className={classNames(sharedStyle.videogameSpecificStatsTable, style.dota2HeroesTable)}
            key={index}
          >
            <div
              className={classNames(
                sharedStyle.videogameSpecificStatsTableHeader,
                style.dota2HeroLine
              )}
            >
              <div className={style.dota2HeroesColumn}>
                {t('block-players_head_to_head-dota2-heroes')}
              </div>
              <div className={style.dota2StatsColumn}>
                {t('block-players_head_to_head-dota2-stats')}
              </div>
            </div>
            {playerMostPickedHeroes.length === 0 ? (
              <div className={sharedStyle.videogameSpecificStatsTableNoContent}>
                {t('error_no-data_compact')}
              </div>
            ) : (
              <div className={sharedStyle.videogameSpecificStatsTableContent}>
                {playerMostPickedHeroes.map(hero => (
                  <div key={hero.id} className={style.dota2HeroLine}>
                    <div className={style.dota2HeroesColumn}>
                      <img
                        src={getCDNUrl(hero.image_url)}
                        alt={localizedHeroName(hero, 'dota2', t)}
                        className={style.dota2HeroImage}
                      />
                    </div>
                    <div className={style.dota2StatsColumn}>
                      <div className={style.dota2HeroName}>
                        {localizedHeroName(hero, 'dota2', t)}
                      </div>
                      <div className={style.dota2StatsContainer}>
                        <div>
                          <div className={style.dota2StatsName}>
                            {t('block-players_head_to_head-dota2-picks')}
                          </div>
                          <div className={style.dota2StatsValue}>{hero.number_of_picks}</div>
                        </div>
                        <div>
                          <div className={style.dota2StatsName}>
                            {t('block-players_head_to_head-dota2-winrate')}
                          </div>
                          <div className={style.dota2StatsValue}>
                            {hero.winrate !== null
                              ? roundForDisplay(0, '%')(hero.winrate * 100)
                              : '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};

Dota2PlayersHeroesStats.displayName = 'Dota2PlayersHeroesStats';

export default Dota2PlayersHeroesStats;
